import './caseStudy.css'
import Footer from "./footer"
import RevealTitle from "./RevealTitle"
import cover from "../Images/endviewCover.png"

const PROBLEMS = [
    "Scheduling human interviewers is slow and costly.",
    "Bias and inconsistency across different interviewers.",
    "No objective, repeatable scoring system for candidates.",
    "Managing invite-only positions is hard at scale.",
];

const SOLUTION = [
    "An AI voice interviewer available 24/7.",
    "Consistent, structured questioning every time.",
    "A Groq LLM that scores 5 categories instantly.",
    "An admin dashboard with leaderboard and invite control.",
];

const WORKFLOW = [
    { n: "01", h: "Admin creates a position", b: "Title, level, tech stack, questions, visibility, and invite emails. Questions can be AI-generated (Groq) and edited, or left for the interviewer to drive live." },
    { n: "02", h: "Candidate discovers it", b: "Visible on the home page, or received through a styled email invitation (Resend)." },
    { n: "03", h: "Voice interview starts", b: "The browser connects to Vapi over WebRTC — a real-time voice call begins, no phone needed." },
    { n: "04", h: "AI conducts the interview", b: "GPT-4 (through Vapi) asks questions, listens, and responds naturally in real time." },
    { n: "05", h: "Groq generates feedback", b: "On call-end, the transcript is sent to Groq and scored across 5 categories instantly." },
    { n: "06", h: "Admin views the leaderboard", b: "Ranked candidates with scores and detailed feedback reports." },
];

const VOICE_FLOW = ["Candidate speaks", "Deepgram · ASR", "GPT-4 reasons", "ElevenLabs · TTS", "Transcript saved"];

const CATEGORIES = ["Communication Skills", "Technical Knowledge", "Problem Solving", "Cultural Fit", "Confidence & Clarity"];

const FEATURES = [
    { h: "AI voice interviews", b: "Vapi-powered real-time voice calls with a professional AI interviewer, right in the browser." },
    { h: "Instant AI feedback", b: "Groq's llama-4-scout scores candidates across 5 categories and writes a full report." },
    { h: "Job positions", b: "Admins create positions with a description, tech stack, and questions." },
    { h: "Invite control", b: "Public or invite-only visibility, set per position." },
    { h: "Email invitations", b: "Resend delivers styled HTML invitation emails automatically." },
    { h: "Recruiter dashboard", b: "A leaderboard with scores, reports, and full position management." },
];

const STACK = [
    { title: "Frontend", items: ["Next.js 16", "TypeScript", "React", "Tailwind CSS"] },
    { title: "Backend", items: ["Server Actions", "Firebase Admin", "Firestore", "REST routes"] },
    { title: "AI & Voice", items: ["Vapi", "GPT-4", "Groq · llama-4-scout", "Vercel AI SDK", "Deepgram", "ElevenLabs"] },
    { title: "Infra", items: ["Firebase Auth", "Vercel", "Resend", "Zod"] },
];

export default function Endview() {
    return (
        <>
            <div className="cs-wrap">
                <header className="cs-hero">
                    <p className="cs-eyebrow">Case study · AI · Etisalat (e&amp;view)</p>
                    <RevealTitle text="EndView" className="cs-title" />
                    <p className="cs-subtitle">
                        An AI-powered voice-interview platform — real-time interviews, instant scored feedback, and a recruiter dashboard.
                    </p>
                </header>

                <div className="cs-links">
                    <a className="cs-linkBtn" href="https://endview.vercel.app" target="_blank" rel="noreferrer">Live demo ↗</a>
                    <a className="cs-linkBtn" href="https://github.com/mohammed0Swalih/endview" target="_blank" rel="noreferrer">GitHub ↗</a>
                </div>

                <div className="cs-cover">
                    <img src={cover} alt="EndView (e&view) — AI interview platform landing page" loading="lazy" />
                </div>

                <div className="cs-meta">
                    <div><p className="cs-meta-label">Role</p><p className="cs-meta-value">Full-stack &amp; AI (solo)</p></div>
                    <div><p className="cs-meta-label">Context</p><p className="cs-meta-value">Etisalat internship · e&amp;view</p></div>
                    <div><p className="cs-meta-label">Type</p><p className="cs-meta-value">AI voice-interview platform</p></div>
                    <div><p className="cs-meta-label">Status</p><p className="cs-meta-value">Live on Vercel</p></div>
                </div>

                {/* Overview */}
                <section className="cs-section">
                    <h2 className="cs-label">Overview</h2>
                    <p className="cs-statement">Interview prep and screening are slow, biased, and hard to score.</p>
                    <div className="cs-cols">
                        <div className="cs-card">
                            <h3 className="cs-card-title">The problem</h3>
                            <ul className="cs-list">{PROBLEMS.map((p) => <li key={p}>{p}</li>)}</ul>
                        </div>
                        <div className="cs-card">
                            <h3 className="cs-card-title">The solution</h3>
                            <ul className="cs-list">{SOLUTION.map((s) => <li key={s}>{s}</li>)}</ul>
                        </div>
                    </div>
                </section>

                {/* How it works */}
                <section className="cs-section">
                    <h2 className="cs-label">How it works</h2>
                    <p className="cs-statement">From a posted role to a scored candidate — end to end.</p>
                    <div className="cs-steps">
                        {WORKFLOW.map((s) => (
                            <div className="cs-step" key={s.n}>
                                <span className="cs-step-n">{s.n}</span>
                                <div>
                                    <h4 className="cs-item-head">{s.h}</h4>
                                    <p className="cs-item-body">{s.b}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Voice pipeline */}
                <section className="cs-section">
                    <h2 className="cs-label">The voice pipeline</h2>
                    <p className="cs-statement">Vapi handles speech-to-text, reasoning, and text-to-speech in one real-time loop.</p>
                    <div className="cs-flow">
                        {VOICE_FLOW.map((step, i) => (
                            <span className="cs-flow-item" key={step}>
                                {step}
                                {i < VOICE_FLOW.length - 1 && <span className="cs-flow-arrow">→</span>}
                            </span>
                        ))}
                    </div>
                    <p className="cs-body">
                        On the client, <code>Agent.tsx</code> listens for Vapi events: <code>call-start</code> flips the
                        status to active, each final <code>message</code> transcript line is appended to state, and
                        <code> call-end</code> triggers feedback generation — with a webhook fallback so a report is still
                        produced even if the browser drops.
                    </p>
                </section>

                {/* Feedback */}
                <section className="cs-section">
                    <h2 className="cs-label">Feedback generation</h2>
                    <p className="cs-statement">A structured score, not a vague summary.</p>
                    <p className="cs-body">
                        When the call ends, Vapi fires an <code>end-of-call-report</code> webhook. The transcript is sent to
                        Groq's <code>llama-4-scout</code> via <code>generateObject()</code>, and a <strong>Zod schema forces
                        structured JSON output</strong> — so scores and comments save cleanly to Firestore every time.
                    </p>
                    <div className="cs-figure"><div className="cs-ph">Feedback report — screenshot</div></div>
                    <p className="cs-item-head" style={{ marginTop: 'clamp(24px,3vw,32px)' }}>Scored across 5 categories</p>
                    <div className="cs-chips">{CATEGORIES.map((c) => <span className="cs-chip" key={c}>{c}</span>)}</div>
                </section>

                {/* Roles */}
                <section className="cs-section">
                    <h2 className="cs-label">Two user roles</h2>
                    <div className="cs-cols">
                        <div className="cs-card">
                            <h3 className="cs-card-title">Admin · Recruiter</h3>
                            <ul className="cs-list">
                                <li>Create positions with title, level, and tech stack.</li>
                                <li>Generate AI questions or write custom ones.</li>
                                <li>Set visibility: public or invite-only.</li>
                                <li>Send email invitations to specific candidates.</li>
                                <li>View the candidate leaderboard with scores.</li>
                                <li>Open or close positions anytime.</li>
                            </ul>
                        </div>
                        <div className="cs-card">
                            <h3 className="cs-card-title">Candidate</h3>
                            <ul className="cs-list">
                                <li>Browse open positions on the home page.</li>
                                <li>View position details: stack, level, type.</li>
                                <li>Start the AI voice interview in the browser.</li>
                                <li>Answer naturally as the AI listens and responds.</li>
                                <li>Receive an instant, detailed feedback report.</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Features */}
                <section className="cs-section">
                    <h2 className="cs-label">Key features</h2>
                    <div className="cs-featureGrid">
                        {FEATURES.map((f) => (
                            <div className="cs-feature" key={f.h}>
                                <h4 className="cs-item-head">{f.h}</h4>
                                <p className="cs-item-body">{f.b}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Stack */}
                <section className="cs-section">
                    <h2 className="cs-label">Tech stack</h2>
                    <div className="cs-stackGroups">
                        {STACK.map((g) => (
                            <div className="cs-stackGroup" key={g.title}>
                                <h4 className="cs-stackTitle">{g.title}</h4>
                                <div className="cs-chips">{g.items.map((it) => <span className="cs-chip" key={it}>{it}</span>)}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* What I built */}
                <section className="cs-section">
                    <h2 className="cs-label">What I built &amp; what's next</h2>
                    <div className="cs-cols">
                        <div className="cs-card">
                            <h3 className="cs-card-title">Delivered</h3>
                            <ul className="cs-list">
                                <li>A full-stack AI interview portal (Next.js + Firebase).</li>
                                <li>Real-time voice interviews (Vapi + GPT-4).</li>
                                <li>Instant feedback via Groq llama-4 with a Zod schema.</li>
                                <li>Admin dashboard with position management + leaderboard.</li>
                                <li>Invite-only visibility with Resend email notifications.</li>
                                <li>Webhook-fallback feedback, even if the client drops.</li>
                            </ul>
                        </div>
                        <div className="cs-card">
                            <h3 className="cs-card-title">Future work</h3>
                            <ul className="cs-list">
                                <li>Phone-number interviews.</li>
                                <li>Multi-company support.</li>
                                <li>Resume parsing and ATS.</li>
                                <li>Video interview mode.</li>
                                <li>Analytics and reporting.</li>
                            </ul>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </>
    )
}
